import React, { useState, useEffect } from 'react';
import config from '../../config';
import { formatJEDescription } from '../../utils/formatJEDescription';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import { useLanguage } from '../../contexts/LanguageContext';
import { JournalEntryDto } from '../../services/journalEntries.service';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';

interface JournalEntryPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  entry: JournalEntryDto | null;
}

const JournalEntryPreviewDialog: React.FC<JournalEntryPreviewDialogProps> = ({
  open,
  onClose,
  entry
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    if (open) {
      loadCompanyInfo();
    }
  }, [open]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  if (!entry) return null;

  const handlePrint = () => {
    const printContent = document.getElementById('journal-entry-print-area');
    if (!printContent) return;

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (!printWindow) return;

    printWindow.document.write(`
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <title>${entry.entryNumber}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; padding: 20px; direction: ${isRTL ? 'rtl' : 'ltr'}; color: #333; }
          .header { text-align: center; border-bottom: 3px solid #667eea; padding-bottom: 15px; margin-bottom: 20px; }
          .header img { max-height: 60px; margin-bottom: 8px; }
          .header h2 { color: #667eea; margin: 5px 0; }
          .header .subtitle { font-size: 12px; color: #666; }
          .entry-title { text-align: center; margin-bottom: 20px; }
          .entry-title h3 { color: #667eea; font-size: 22px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; border: 2px solid #667eea; border-radius: 8px; padding: 15px; margin-bottom: 20px; }
          .info-item label { font-size: 11px; color: #888; display: block; }
          .info-item span { font-weight: bold; font-size: 14px; }
          .status-badge { display: inline-block; padding: 2px 12px; border-radius: 12px; font-size: 12px; font-weight: bold; color: white; }
          .status-posted { background: #4caf50; }
          .status-draft { background: #ff9800; }
          .status-cancelled { background: #f44336; }
          .section { margin-bottom: 15px; border: 1px solid #ddd; border-radius: 6px; overflow: hidden; }
          .section-header { padding: 8px 15px; color: white; font-weight: bold; font-size: 14px; }
          .section-header.debit { background: #4caf50; }
          .section-header.credit { background: #ff9800; }
          table { width: 100%; border-collapse: collapse; }
          th { background: #f5f5f5; padding: 8px 10px; font-size: 12px; text-align: ${isRTL ? 'right' : 'left'}; border-bottom: 2px solid #ddd; }
          th.amount { text-align: ${isRTL ? 'left' : 'right'}; }
          td { padding: 8px 10px; font-size: 12px; border-bottom: 1px solid #eee; }
          td.amount { text-align: ${isRTL ? 'left' : 'right'}; font-weight: bold; }
          td.amount.debit { color: #4caf50; }
          td.amount.credit { color: #ff9800; }
          .account-code { font-weight: bold; }
          .account-name { font-size: 11px; color: #666; }
          .total-row td { background: rgba(0,0,0,0.05); font-weight: bold; font-size: 13px; }
          .balance-box { border: 2px solid #667eea; border-radius: 8px; padding: 12px 20px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
          .balance-box h4 { font-size: 16px; }
          .balance-check { font-size: 24px; }
          .signatures { display: flex; justify-content: space-between; margin-top: 60px; }
          .sig-block { text-align: center; width: 30%; }
          .sig-line { border-top: 1px solid #333; padding-top: 8px; margin-top: 60px; font-weight: bold; font-size: 13px; }
          .description-box { margin-top: 10px; padding: 8px; background: #f9f9f9; border-radius: 4px; }
          .description-box label { font-size: 11px; color: #888; font-weight: bold; }
          .description-box p { font-size: 13px; margin-top: 3px; }
          @media print { @page { size: A4; margin: 1cm; } }
        </style>
      </head>
      <body>
        ${companyInfo ? `
        <div class="header">
          ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" onerror="this.style.display='none'" />` : ''}
          <h2>${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
          <div class="subtitle">
            ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
          </div>
        </div>
        ` : ''}

        <div class="entry-title">
          <h3>${isRTL ? 'قيد يومية' : 'Journal Entry'}</h3>
        </div>

        <div class="info-grid">
          <div class="info-item">
            <label>${isRTL ? 'رقم القيد' : 'Entry Number'}</label>
            <span style="color: #667eea;">${entry.entryNumber}</span>
          </div>
          <div class="info-item">
            <label>${isRTL ? 'التاريخ' : 'Date'}</label>
            <span>${new Date(entry.entryDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}</span>
          </div>
          <div class="info-item">
            <label>${isRTL ? 'الحالة' : 'Status'}</label>
            <span class="status-badge status-${entry.status.toLowerCase()}">${entry.status === 'Posted' ? (isRTL ? 'مرحل' : 'Posted') : entry.status === 'Draft' ? (isRTL ? 'مسودة' : 'Draft') : (isRTL ? 'ملغي' : 'Cancelled')}</span>
          </div>
          <div class="info-item">
            <label>${isRTL ? 'النوع' : 'Type'}</label>
            <span>${entry.entryType === 'Manual' ? (isRTL ? 'يدوي' : 'Manual') : entry.entryType || (isRTL ? 'تلقائي' : 'Auto')}</span>
          </div>
          <div class="info-item">
            <label>${isRTL ? 'الفترة المالية' : 'Fiscal Period'}</label>
            <span>${entry.fiscalPeriodName || '-'}</span>
          </div>
          <div class="info-item">
            <label>${isRTL ? 'رقم القيد الداخلي' : 'Internal ID'}</label>
            <span>#${entry.journalEntryId}</span>
          </div>
          ${entry.supplierInvoiceNumber ? `
          <div class="info-item">
            <label>${isRTL ? 'رقم فاتورة المورد' : 'Supplier Invoice No.'}</label>
            <span>${entry.supplierInvoiceNumber}</span>
          </div>
          ` : ''}
          ${entry.description ? `
          <div class="info-item" style="grid-column: span 2;">
            <label>${isRTL ? 'الوصف' : 'Description'}</label>
            <span>${isRTL ? entry.descriptionAr || entry.description : entry.description}</span>
          </div>
          ` : ''}
        </div>

        <div class="section">
          <div class="section-header debit">${isRTL ? '📥 المدين (Debit)' : '📥 Debit'}</div>
          <table>
            <thead>
              <tr>
                <th>${isRTL ? 'الحساب' : 'Account'}</th>
                <th>${isRTL ? 'مركز التكلفة' : 'Cost Center'}</th>
                <th>${isRTL ? 'البيان' : 'Description'}</th>
                <th class="amount">${isRTL ? 'المبلغ' : 'Amount'}</th>
              </tr>
            </thead>
            <tbody>
              ${entry.details.filter(d => d.debit > 0).map(d => `
              <tr>
                <td><span class="account-code">${d.accountCode}</span><br/><span class="account-name">${isRTL ? d.accountNameAr || d.accountName : d.accountName}</span></td>
                <td>${d.costCenterCode ? `<span class="account-code">${d.costCenterCode}</span>${d.costCenterName ? `<br/><span class="account-name">${d.costCenterName}</span>` : ''}` : '-'}</td>
                <td>${formatJEDescription(d.description, isRTL, d.accountName, d.accountNameAr)}</td>
                <td class="amount debit">${d.debit.toFixed(3)}</td>
              </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="3" style="text-align: ${isRTL ? 'left' : 'right'};">${isRTL ? 'إجمالي المدين:' : 'Total Debit:'}</td>
                <td class="amount debit">${entry.totalDebit.toFixed(3)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="section">
          <div class="section-header credit">${isRTL ? '📤 الدائن (Credit)' : '📤 Credit'}</div>
          <table>
            <thead>
              <tr>
                <th>${isRTL ? 'الحساب' : 'Account'}</th>
                <th>${isRTL ? 'مركز التكلفة' : 'Cost Center'}</th>
                <th>${isRTL ? 'البيان' : 'Description'}</th>
                <th class="amount">${isRTL ? 'المبلغ' : 'Amount'}</th>
              </tr>
            </thead>
            <tbody>
              ${entry.details.filter(d => d.credit > 0).map(d => `
              <tr>
                <td><span class="account-code">${d.accountCode}</span><br/><span class="account-name">${isRTL ? d.accountNameAr || d.accountName : d.accountName}</span></td>
                <td>${d.costCenterCode ? `<span class="account-code">${d.costCenterCode}</span>${d.costCenterName ? `<br/><span class="account-name">${d.costCenterName}</span>` : ''}` : '-'}</td>
                <td>${formatJEDescription(d.description, isRTL, d.accountName, d.accountNameAr)}</td>
                <td class="amount credit">${d.credit.toFixed(3)}</td>
              </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="3" style="text-align: ${isRTL ? 'left' : 'right'};">${isRTL ? 'إجمالي الدائن:' : 'Total Credit:'}</td>
                <td class="amount credit">${entry.totalCredit.toFixed(3)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="balance-box">
          <h4>${isRTL ? 'التوازن' : 'Balance'}</h4>
          <div>
            ${entry.totalDebit.toFixed(3)} = ${entry.totalCredit.toFixed(3)}
            <span class="balance-check">${Math.abs(entry.totalDebit - entry.totalCredit) < 0.01 ? ' ✅' : ' ❌'}</span>
          </div>
        </div>

        <div class="signatures">
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المحاسب' : 'Accountant'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المدير المالي' : 'Financial Manager'}</div></div>
          <div class="sig-block"><div class="sig-line">${isRTL ? 'المدير العام' : 'General Manager'}</div></div>
        </div>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      // printWindow stays open for user review
    }, 500);
  };

  const isDraft = entry.status === 'Draft';
  const isPosted = entry.status === 'Posted';
  const isCancelled = entry.status === 'Cancelled';

  const debitDetails = entry.details.filter(d => d.debit > 0);
  const creditDetails = entry.details.filter(d => d.credit > 0);

  const getStatusLabel = () => {
    if (isDraft) return isRTL ? 'مسودة' : 'Draft';
    if (isPosted) return isRTL ? 'مرحل' : 'Posted';
    if (isCancelled) return isRTL ? 'ملغي' : 'Cancelled';
    return entry.status;
  };

  const getStatusColor = () => {
    if (isDraft) return 'warning';
    if (isPosted) return 'success';
    if (isCancelled) return 'error';
    return 'default';
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: isDarkMode ? '#1a1a2e' : 'white'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: isDarkMode
            ? 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2
        }}
      >
        <Box component="span">
          {isRTL ? 'معاينة القيد المحاسبي' : 'Journal Entry Preview'}
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }} id="journal-entry-print-area">
        {/* Header Info */}
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mb: 3,
            bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5',
            borderRadius: 2
          }}
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'رقم القيد' : 'Entry Number'}
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {entry.entryNumber}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'التاريخ' : 'Date'}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {new Date(entry.entryDate).toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'الحالة' : 'Status'}
              </Typography>
              <Box>
                <Chip
                  label={getStatusLabel()}
                  color={getStatusColor()}
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              </Box>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'النوع' : 'Type'}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {entry.entryType === 'Manual' ? (isRTL ? 'يدوي' : 'Manual') : entry.entryType || (isRTL ? 'تلقائي' : 'Auto')}
              </Typography>
            </Box>
            {entry.supplierInvoiceNumber && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {isRTL ? 'رقم فاتورة المورد' : 'Supplier Invoice No.'}
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {entry.supplierInvoiceNumber}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'الفترة المالية' : 'Fiscal Period'}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {entry.fiscalPeriodName || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                {isRTL ? 'رقم القيد الداخلي' : 'Internal ID'}
              </Typography>
              <Typography variant="body1" fontWeight="bold" color="text.secondary">
                #{entry.journalEntryId}
              </Typography>
            </Box>
          </Box>

          {entry.description && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                {isRTL ? 'الوصف:' : 'Description:'}
              </Typography>
              <Typography variant="body2">
                {isRTL ? entry.descriptionAr || entry.description : entry.description}
              </Typography>
            </Box>
          )}

          {entry.notes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                {isRTL ? 'ملاحظات:' : 'Notes:'}
              </Typography>
              <Typography variant="body2">
                {entry.notes}
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Debit Section */}
        <Paper
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: isDarkMode ? 'rgba(76,175,80,0.1)' : 'white'
          }}
        >
          <Box sx={{ bgcolor: isDarkMode ? 'rgba(76,175,80,0.3)' : '#4caf50', color: 'white', p: 1.5, fontWeight: 'bold' }}>
            {isRTL ? '📥 المدين (Debit)' : '📥 Debit'}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الحساب' : 'Account'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'مركز التكلفة' : 'Cost Center'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'البيان' : 'Description'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'المبلغ' : 'Amount'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {debitDetails.map((detail, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      <strong>{detail.accountCode}</strong>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {isRTL ? detail.accountNameAr || detail.accountName : detail.accountName}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {detail.costCenterCode ? (
                        <>
                          {detail.costCenterCode}
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {detail.costCenterName}
                          </Typography>
                        </>
                      ) : '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{formatJEDescription(detail.description, isRTL, detail.accountName, detail.accountNameAr)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#4caf50' }}>
                      {detail.debit.toFixed(3)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold', bgcolor: 'rgba(76,175,80,0.1)', fontSize: '0.85rem' }}>
                    {isRTL ? 'إجمالي المدين:' : 'Total Debit:'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'rgba(76,175,80,0.1)' }}>
                    <Typography variant="h6" sx={{ color: '#4caf50' }}>{entry.totalDebit.toFixed(3)}</Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Credit Section */}
        <Paper
          elevation={2}
          sx={{
            mb: 2,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: isDarkMode ? 'rgba(255,152,0,0.1)' : 'white'
          }}
        >
          <Box sx={{ bgcolor: isDarkMode ? 'rgba(255,152,0,0.3)' : '#ff9800', color: 'white', p: 1.5, fontWeight: 'bold' }}>
            {isRTL ? '📤 الدائن (Credit)' : '📤 Credit'}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'الحساب' : 'Account'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'مركز التكلفة' : 'Cost Center'}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'البيان' : 'Description'}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{isRTL ? 'المبلغ' : 'Amount'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {creditDetails.map((detail, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      <strong>{detail.accountCode}</strong>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {isRTL ? detail.accountNameAr || detail.accountName : detail.accountName}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>
                      {detail.costCenterCode ? (
                        <>
                          {detail.costCenterCode}
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            {detail.costCenterName}
                          </Typography>
                        </>
                      ) : '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem' }}>{formatJEDescription(detail.description, isRTL, detail.accountName, detail.accountNameAr)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#ff9800' }}>
                      {detail.credit.toFixed(3)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold', bgcolor: 'rgba(255,152,0,0.1)', fontSize: '0.85rem' }}>
                    {isRTL ? 'إجمالي الدائن:' : 'Total Credit:'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'rgba(255,152,0,0.1)' }}>
                    <Typography variant="h6" sx={{ color: '#ff9800' }}>{entry.totalCredit.toFixed(3)}</Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Balance Check */}
        <Paper
          elevation={3}
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: Math.abs(entry.totalDebit - entry.totalCredit) < 0.01
              ? (isDarkMode ? 'rgba(76,175,80,0.2)' : '#e8f5e9')
              : (isDarkMode ? 'rgba(244,67,54,0.2)' : '#ffebee')
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold">
              {isRTL ? 'التوازن' : 'Balance'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Typography variant="h6">
                {entry.totalDebit.toFixed(3)} = {entry.totalCredit.toFixed(3)}
              </Typography>
              <Typography variant="h4">
                {Math.abs(entry.totalDebit - entry.totalCredit) < 0.01 ? '✅' : '❌'}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
        <Button onClick={onClose} size="large">
          {isRTL ? 'إغلاق' : 'Close'}
        </Button>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          size="large"
          sx={{
            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
            '&:hover': { background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)' }
          }}
        >
          {isRTL ? 'طباعة' : 'Print'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JournalEntryPreviewDialog;